import {
  Locale,
  addDays,
  addMonths,
  format,
  formatDistanceStrict,
  isValid,
  startOfDay,
} from 'date-fns';
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict';
import locale from 'date-fns/locale/en-US';

export { addDays, addMonths, isValid, startOfDay };

export const DATE_FORMAT = 'yyyy-MM-dd';

export const DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';
export const SHORT_DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm';
export const HUMAN_DATE_FORMAT = 'd MMMM yyyy';
export const SHORT_DATE_FORMAT = 'MMM dd';
export const MONTH_DAY_FORMAT = 'MMM d';
export const SHORT_MONTH_DAY_TIME_FORMAT = 'MMM dd, HH:mm';
export const TIME_FORMAT = 'h:mm a';

export function toDateString(timestamp: Date, formatting?: string): string;
export function toDateString(
  timestamp: string | number,
  formatting?: string,
): string | undefined;
export function toDateString(
  timestamp: string | number | Date,
  formatting: string = DATE_FORMAT,
): string | undefined {
  const date =
    typeof timestamp === 'number' || typeof timestamp === 'string'
      ? new Date(timestamp)
      : timestamp;
  return isValid(date) ? format(date, formatting) : undefined;
}

export function toApproxDateString(
  timestamp: string | number | Date,
  formatting?: string,
): string | undefined {
  const formatted = toDateString(timestamp as string | number, formatting);
  return formatted ? `~${formatted}` : undefined;
}

export function toISOStringUTC(timestamp: Date): string;
export function toISOStringUTC(timestamp: string | number): string | undefined;
export function toISOStringUTC(
  timestamp: string | number | Date,
): string | undefined {
  const date =
    typeof timestamp === 'number' || typeof timestamp === 'string'
      ? new Date(timestamp)
      : timestamp;

  if (!isValid(date)) {
    return undefined;
  }

  const isoString = date.toISOString();
  const seconds = date.getUTCSeconds();

  // If seconds are 00, exclude them from the output
  if (seconds === 0) {
    return `${isoString.slice(0, -8)}Z`;
  }

  return `${isoString.slice(0, -5)}Z`;
}

export const xAgo = (target: Date, now: Date = new Date()): string => {
  const delta = now.getTime() - target.getTime();
  const justNow = Math.abs(delta) < 60 * 1000;

  if (justNow) {
    return 'just now';
  }
  return formatDistanceStrict(target, now, {
    addSuffix: true,
  });
};

// https://github.com/date-fns/date-fns/issues/1706#issuecomment-836601089
const shortFormat = {
  lessThanXSeconds: '{{count}}s',
  xSeconds: '{{count}}s',
  halfAMinute: '30s',
  lessThanXMinutes: '{{count}}m',
  xMinutes: '{{count}}m',
  aboutXHours: '{{count}}h',
  xHours: '{{count}}h',
  xDays: '{{count}}d',
  aboutXWeeks: '{{count}}w',
  xWeeks: '{{count}}w',
  aboutXMonths: '{{count}}mo',
  xMonths: '{{count}}mo',
  aboutXYears: '{{count}}y',
  xYears: '{{count}}y',
  overXYears: '{{count}}y',
  almostXYears: '{{count}}y',
};

const formatDistance: Locale['formatDistance'] = (
  token: keyof typeof shortFormat,
  count,
  options = {},
) => {
  const result = shortFormat[token].replace('{{count}}', count);

  if (options.addSuffix) {
    if (options.comparison > 0) {
      return `In ${result}`;
    } else if (options.comparison < 0) {
      return `${result} ago`;
    }
    return 'Now';
  }

  return result;
};

export const xAgoShort = (target: Date): string => {
  return formatDistanceToNowStrict(target, {
    addSuffix: true,
    locale: {
      ...locale,
      formatDistance,
    },
  });
};

export function formatDuration(
  startTime: string,
  endTime?: string | null,
): string {
  const start = new Date(startTime).getTime();
  const end = endTime ? new Date(endTime).getTime() : Date.now();
  const durationMs = end - start;

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
}

export function formatRuntime(startTime: string, endTime: string): string {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end.getTime() - start.getTime();

  if (diffMs < 0) return '—';

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffHours > 0) {
    const remainingMinutes = diffMinutes % 60;
    return `${diffHours}h ${remainingMinutes}m`;
  }
  if (diffMinutes > 0) {
    const remainingSeconds = diffSeconds % 60;
    return `${diffMinutes}m ${remainingSeconds}s`;
  }
  return `${diffSeconds}s`;
}

export const formatDurationMs = (ms: number): string => {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
};

export const getDurationUnit = (duration: string): string => {
  return duration.match(/[MHD]/)?.[0] || 'D';
};
